import {
  DATE_FORMATS,
  DENSITIES,
  ICON_STYLES,
  LANGUAGE_LEVELS,
  SECTION_KINDS,
  SKILL_LEVELS,
  STARTER_ORDER,
  TEXT_SIZE_MAX,
  TEXT_SIZE_MIN,
  isBuiltIn,
  normalizeTypeface,
  schemaOf,
  BUILT_IN,
  BUILT_IN_TITLES,
  type CertificationEntry,
  type CustomSection,
  type CustomSectionItem,
  type DateFormat,
  type EducationEntry,
  type ExperienceEntry,
  type ISODate,
  type LanguageEntry,
  type PersonalDetails,
  type ProjectEntry,
  type Resume,
  type ResumeDesign,
  type ResumeLink,
  type SectionKind,
  type SkillEntry,
} from "./types";

// Constructors, lenient JSON reading (every reader falls back to a default,
// like the app's json_utils.dart — older files simply lack newer keys) and
// date helpers.

export function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Local time as Dart's DateTime.toIso8601String() writes it (no zone). */
export function nowIso(): ISODate {
  const d = new Date();
  const p = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
}

/** The first of [month] (1–12) in [year], as an ISO date. */
export function monthIso(year: number, month = 1): ISODate {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01T00:00:00.000`;
}

/** Year and month (1–12) of an ISO date, read from the string (no time zones involved). */
export function ymOf(iso: ISODate | null | undefined): { year: number; month: number } | null {
  const m = typeof iso === "string" ? /^(\d{4})-(\d{2})/.exec(iso) : null;
  if (!m) return null;
  const month = Number(m[2]);
  return { year: Number(m[1]), month: month >= 1 && month <= 12 ? month : 1 };
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function monthName(month: number, long = false): string {
  return (long ? MONTHS_LONG : MONTHS_SHORT)[month - 1] ?? "";
}

/** One date in a pattern ("MMM yyyy" → "Jan 2024"). */
export function formatDate(iso: ISODate | null | undefined, pattern: DateFormat): string {
  const d = ymOf(iso);
  if (!d) return "";
  switch (pattern) {
    case "MMMM yyyy":
      return `${monthName(d.month, true)} ${d.year}`;
    case "MM/yyyy":
      return `${String(d.month).padStart(2, "0")}/${d.year}`;
    case "yyyy":
      return String(d.year);
    default:
      return `${monthName(d.month)} ${d.year}`;
  }
}

/** "Jan 2020 – Present"; one date alone when the other is missing or equal. */
export function formatRange(start: ISODate | null, end: ISODate | null, current: boolean, pattern: DateFormat): string {
  const s = formatDate(start, pattern);
  const e = current ? "Present" : formatDate(end, pattern);
  if (!s) return e;
  if (!e || e === s) return s;
  return `${s} – ${e}`;
}

// ---- lenient readers -------------------------------------------------------------

type Json = Record<string, unknown>;

const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const optStr = (v: unknown): string | null => (typeof v === "string" && v.length ? v : null);
const bool = (v: unknown, fallback = false): boolean => (typeof v === "boolean" ? v : fallback);
const date = (v: unknown): ISODate | null => (typeof v === "string" && ymOf(v) ? v : null);
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const list = <T>(v: unknown, parse: (j: Json) => T): T[] =>
  Array.isArray(v) ? v.filter((x): x is Json => !!x && typeof x === "object").map(parse) : [];
const id = (v: unknown): string => (typeof v === "string" && v ? v : newId());
const strMap = (v: unknown): Record<string, string> => {
  const out: Record<string, string> = {};
  if (v && typeof v === "object") for (const [k, x] of Object.entries(v)) if (typeof x === "string") out[k] = x;
  return out;
};
const argb = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? (v | 0xff000000) >>> 0 : null);

export function parseLink(j: Json): ResumeLink {
  return { id: id(j.id), label: str(j.label), url: str(j.url) };
}

export function parsePersonal(j: Json): PersonalDetails {
  return {
    fullName: str(j.fullName),
    headline: str(j.headline),
    email: str(j.email),
    phone: str(j.phone),
    location: str(j.location),
    photoPath: optStr(j.photoPath),
    photo: typeof j.photo === "string" && j.photo.startsWith("data:image/") ? j.photo : null,
    website: optStr(j.website),
    linkedIn: optStr(j.linkedIn),
    github: optStr(j.github),
    links: list(j.links, parseLink),
    dateOfBirth: str(j.dateOfBirth),
    nationality: str(j.nationality),
    summary: str(j.summary),
    objective: str(j.objective),
  };
}

export function parseExperience(j: Json): ExperienceEntry {
  return {
    id: id(j.id),
    jobTitle: str(j.jobTitle),
    company: str(j.company),
    location: str(j.location),
    employmentType: str(j.employmentType),
    startDate: date(j.startDate),
    endDate: date(j.endDate),
    isCurrent: bool(j.isCurrent),
    description: str(j.description),
    bulletPoints: strList(j.bulletPoints),
  };
}

export function parseEducation(j: Json): EducationEntry {
  return {
    id: id(j.id),
    institution: str(j.institution),
    degree: str(j.degree),
    fieldOfStudy: str(j.fieldOfStudy),
    location: str(j.location),
    startDate: date(j.startDate),
    endDate: date(j.endDate),
    isCurrent: bool(j.isCurrent),
    grade: str(j.grade),
    description: str(j.description),
  };
}

export function parseSkill(j: Json): SkillEntry {
  const level = SKILL_LEVELS.find((l) => l === j.level) ?? "intermediate";
  return { id: id(j.id), name: str(j.name), level, category: str(j.category) };
}

export function parseProject(j: Json): ProjectEntry {
  return {
    id: id(j.id),
    name: str(j.name),
    role: str(j.role),
    description: str(j.description),
    technologies: strList(j.technologies),
    link: optStr(j.link),
    startDate: date(j.startDate),
    endDate: date(j.endDate),
    isCurrent: bool(j.isCurrent),
  };
}

export function parseCertification(j: Json): CertificationEntry {
  return {
    id: id(j.id),
    name: str(j.name),
    issuer: str(j.issuer),
    issueDate: date(j.issueDate),
    credentialId: str(j.credentialId),
    credentialUrl: optStr(j.credentialUrl),
  };
}

export function parseLanguage(j: Json): LanguageEntry {
  const proficiency = LANGUAGE_LEVELS.find((l) => l === j.proficiency) ?? "conversational";
  return { id: id(j.id), name: str(j.name), proficiency };
}

export function parseItem(j: Json): CustomSectionItem {
  return {
    id: id(j.id),
    heading: str(j.heading),
    subheading: str(j.subheading),
    location: str(j.location),
    description: str(j.description),
    url: str(j.url),
    date: date(j.date),
    endDate: date(j.endDate),
    isCurrent: bool(j.isCurrent),
  };
}

export function parseCustomSection(j: Json): CustomSection {
  const title = str(j.title, "Custom Section");
  const kind = SECTION_KINDS.find((k) => k === j.kind) ?? (title === "Achievements" ? "achievements" : "custom");
  return { id: id(j.id), title, kind, items: list(j.items, parseItem) };
}

export function parseDesign(j: Json): ResumeDesign {
  const heading = typeof j.headingFont === "string" ? normalizeTypeface(j.headingFont) : null;
  const style = ICON_STYLES.find((s) => s === j.iconStyle);
  return {
    accentColor: argb(j.accentColor),
    textColor: argb(j.textColor),
    headingFont: heading === null || heading === "default" ? null : heading,
    density: DENSITIES.find((d) => d === j.density) ?? "normal",
    dateFormat: DATE_FORMATS.find((d) => d === j.dateFormat) ?? "MMM yyyy",
    showPhoto: bool(j.showPhoto, true),
    showSkillLevels: bool(j.showSkillLevels),
    // Older saves only have the on/off switch.
    iconStyle: style ?? (bool(j.showIcons, true) ? "filled" : "none"),
    sectionIcons: bool(j.sectionIcons),
    sectionIconChoices: strMap(j.sectionIconChoices),
    sectionTitles: strMap(j.sectionTitles),
  };
}

/** Reads a resume from JSON (the app's format), repairing anything missing. */
export function parseResume(raw: unknown): Resume {
  const j = (raw && typeof raw === "object" ? raw : {}) as Json;
  const now = nowIso();
  const fontScale = typeof j.fontScale === "number" ? Math.min(TEXT_SIZE_MAX, Math.max(TEXT_SIZE_MIN, j.fontScale)) : 1;
  return {
    id: id(j.id),
    title: str(j.title, "Untitled Resume") || "Untitled Resume",
    templateId: optStr(j.templateId),
    pageSize: j.pageSize === "letter" ? "letter" : "a4",
    fontFamily: normalizeTypeface(typeof j.fontFamily === "string" ? j.fontFamily : null),
    fontScale,
    design: parseDesign((j.design && typeof j.design === "object" ? j.design : {}) as Json),
    personalDetails: parsePersonal((j.personalDetails && typeof j.personalDetails === "object" ? j.personalDetails : {}) as Json),
    experience: list(j.experience, parseExperience),
    education: list(j.education, parseEducation),
    skills: list(j.skills, parseSkill),
    projects: list(j.projects, parseProject),
    certifications: list(j.certifications, parseCertification),
    languages: list(j.languages, parseLanguage),
    customSections: list(j.customSections, parseCustomSection),
    sectionOrder: Array.isArray(j.sectionOrder) ? strList(j.sectionOrder) : [...BUILT_IN],
    hiddenSections: strList(j.hiddenSections),
    tags: strList(j.tags),
    folder: optStr(j.folder),
    isFavorite: bool(j.isFavorite),
    isArchived: bool(j.isArchived),
    createdAt: date(j.createdAt) ?? now,
    updatedAt: date(j.updatedAt) ?? now,
  };
}

// ---- constructors -------------------------------------------------------------------

export const emptyPersonal = (): PersonalDetails => parsePersonal({});
export const emptyDesign = (): ResumeDesign => parseDesign({});

export function newResume(o: { title?: string; templateId?: string | null } = {}): Resume {
  const now = nowIso();
  return {
    ...parseResume({}),
    id: newId(),
    title: o.title?.trim() || "Untitled Resume",
    templateId: o.templateId ?? null,
    sectionOrder: [...STARTER_ORDER],
    createdAt: now,
    updatedAt: now,
  };
}

export const newExperience = (): ExperienceEntry => parseExperience({});
export const newEducation = (): EducationEntry => parseEducation({});
export const newProject = (): ProjectEntry => parseProject({});
export const newCertification = (): CertificationEntry => parseCertification({});
export const newItem = (): CustomSectionItem => parseItem({});
export const newSkill = (name: string, category = ""): SkillEntry => ({ ...parseSkill({}), name, category });
export const newLanguage = (name: string): LanguageEntry => ({ ...parseLanguage({}), name });
export const newLink = (): ResumeLink => parseLink({});

export function newCustomSection(kind: SectionKind, title: string): CustomSection {
  return { id: newId(), title, kind, items: [] };
}

// ---- queries --------------------------------------------------------------------------

export function customSection(r: Resume, key: string): CustomSection | null {
  return r.customSections.find((s) => s.id === key) ?? null;
}

/**
 * [sectionOrder] cleaned up: no duplicates, no keys of deleted custom
 * sections, and every custom section present.
 */
export function normalizedOrder(r: Resume): string[] {
  const customIds = new Set(r.customSections.map((s) => s.id));
  const seen = new Set<string>();
  const order = r.sectionOrder.filter((k) => (isBuiltIn(k) || customIds.has(k)) && !seen.has(k) && !!seen.add(k));
  for (const s of r.customSections) if (!seen.has(s.id)) order.push(s.id);
  return order;
}

export const isEmptyExperience = (e: ExperienceEntry) =>
  !e.jobTitle.trim() && !e.company.trim() && !e.description.trim() && e.bulletPoints.every((b) => !b.trim());
export const isEmptyEducation = (e: EducationEntry) => !e.institution.trim() && !e.degree.trim() && !e.fieldOfStudy.trim();
export const isEmptyProject = (p: ProjectEntry) => !p.name.trim() && !p.role.trim() && !p.description.trim();
export const isEmptyCertification = (c: CertificationEntry) => !c.name.trim() && !c.issuer.trim();
export const isEmptyItem = (i: CustomSectionItem) => !i.heading.trim() && !i.subheading.trim() && !i.description.trim();

export function sectionHasContent(r: Resume, key: string): boolean {
  switch (key) {
    case "summary":
      return r.personalDetails.summary.trim().length > 0;
    case "experience":
      return r.experience.some((e) => !isEmptyExperience(e));
    case "education":
      return r.education.some((e) => !isEmptyEducation(e));
    case "skills":
      return r.skills.some((s) => s.name.trim().length > 0);
    case "projects":
      return r.projects.some((p) => !isEmptyProject(p));
    case "certifications":
      return r.certifications.some((c) => !isEmptyCertification(c));
    case "languages":
      return r.languages.some((l) => l.name.trim().length > 0);
    default: {
      const s = customSection(r, key);
      // An empty references section still prints "available upon request".
      return !!s && (s.items.some((i) => !isEmptyItem(i)) || s.kind === "references");
    }
  }
}

/** Sections that will actually print, in order. */
export function visibleSections(r: Resume): string[] {
  return normalizedOrder(r).filter((k) => !r.hiddenSections.includes(k) && sectionHasContent(r, k));
}

/** The heading shown for [key] — the user's rename if any. */
export function sectionTitle(r: Resume, key: string): string {
  const renamed = r.design.sectionTitles[key]?.trim();
  if (renamed) return renamed;
  if (isBuiltIn(key)) return BUILT_IN_TITLES[key];
  const custom = customSection(r, key);
  if (!custom) return key;
  return custom.title.trim() || schemaOf(custom.kind).defaultTitle;
}
